import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getWorkflowOptions, getWorkflowQueryKey } from '../../generated/api/@tanstack/react-query.gen.js';
import { RuntimePolicySurface } from '../../generated/api/types.gen.js';
import { usePolicy } from '../../hooks/usePolicy.js';
import { runtimePolicyApi } from '../../services/api/runtime-policy.api.js';
import { RuntimePolicyAssigner } from '../ui/RuntimePolicyAssigner.js';

interface WorkflowPolicyCardProps {
  workflowId: number;
}

/** The runtime policy a workflow runs under; a workflow manager can change it. */
export const WorkflowPolicyCard: React.FC<WorkflowPolicyCardProps> = ({ workflowId }) => {
  const queryClient = useQueryClient();
  const canManage = usePolicy('CanManageWorkflows');
  const { data, isError } = useQuery({
    ...getWorkflowOptions({ path: { id: String(workflowId) } }),
    select: (res) => res.template.runtimePolicyId,
  });
  if (isError) {
    return (
      <div className="shrink-0 rounded-xl border bg-surface px-4 py-3 text-xs text-danger">
        Could not load the workflow’s runtime policy.
      </div>
    );
  }
  if (data === undefined || data === null) return null;
  return (
    <div className="shrink-0 rounded-xl border bg-surface px-4 py-3">
      <RuntimePolicyAssigner
        surface={RuntimePolicySurface.WORKFLOW}
        label="This workflow"
        currentPolicyId={data}
        canChange={canManage}
        onAssign={async (id) => {
          await runtimePolicyApi.setWorkflowPolicy(workflowId, id);
          await queryClient.invalidateQueries({ queryKey: getWorkflowQueryKey({ path: { id: String(workflowId) } }) });
        }}
      />
    </div>
  );
};
